/**
 * M5 stub: Hotspot Navigation Bar — top-center of the HUD.
 *
 * Shows the current hotspot name with prev/next chevrons.
 * Hidden when the scene has no hotspots.
 * Full implementation in M5; this stub renders the correct shape
 * and wires to the hotspot store state.
 *
 * Built on @heroui/react Button + @gravity-ui/icons.
 */
import { useViewerStore } from '@stores/viewerStore';
import { ChevronLeft, ChevronRight } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { useTranslation } from 'react-i18next';

export function HotspotNavBar() {
  const { t } = useTranslation();
  const sceneConfig = useViewerStore((s) => s.sceneConfig);
  const activeIndex = useViewerStore((s) => s.activeHotspotIndex);
  const isOpen = useViewerStore((s) => s.hotspotPanelOpen);
  const setActive = useViewerStore((s) => s.setActiveHotspotIndex);
  const setPanelOpen = useViewerStore((s) => s.setHotspotPanelOpen);

  const hotspots = sceneConfig?.hotspots ?? [];

  // Hide when no hotspots
  if (hotspots.length === 0) return null;

  const currentIndex = activeIndex !== null && activeIndex < hotspots.length ? activeIndex : 0;
  const current = hotspots[currentIndex];

  function prev() {
    const next = (currentIndex - 1 + hotspots.length) % hotspots.length;
    setActive(next);
  }

  function next() {
    const n = (currentIndex + 1) % hotspots.length;
    setActive(n);
  }

  return (
    <div
      className={`w-[464px] overflow-hidden rounded-[20px] bg-black/55 shadow-2xl ring-1 ring-white/10 backdrop-blur-md transition-all duration-200 ${
        isOpen ? 'p-2' : 'px-2 py-1'
      }`}
    >
      <div className="flex h-7 items-center gap-2">
        <Button
          isIconOnly
          size="sm"
          onPress={prev}
          aria-label={t('hotspots.previous')}
          className="size-6 min-w-6 rounded-full bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
        >
          <ChevronLeft width={13} height={13} />
        </Button>

        <button
          type="button"
          onClick={() => setPanelOpen(!isOpen)}
          className="min-w-0 flex-1 text-center text-xs font-semibold text-white outline-none"
          aria-expanded={isOpen}
        >
          <span className="block truncate">{current.title}</span>
        </button>

        <Button
          isIconOnly
          size="sm"
          onPress={next}
          aria-label={t('hotspots.next')}
          className="size-6 min-w-6 rounded-full bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
        >
          <ChevronRight width={13} height={13} />
        </Button>
      </div>

      {isOpen && (
        <div className="mt-2 flex [scrollbar-width:none] gap-2 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden">
          {hotspots.map((hotspot, index) => {
            const active = index === currentIndex;
            return (
              <Button
                key={hotspot.id}
                isIconOnly
                onClick={() => setActive(index)}
                aria-label={t('hotspots.openHotspot', { title: hotspot.title })}
                className={`relative h-25 w-25 overflow-hidden rounded-lg bg-white/10 ring-2 transition-all ${
                  active ? 'ring-[#235FEE]' : 'ring-white/10 hover:ring-white/45'
                }`}
              >
                {hotspot.imageUrl ? (
                  <img
                    src={hotspot.imageUrl}
                    alt=""
                    draggable={false}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="grid h-full w-full place-items-center bg-white/10 text-[10px] font-semibold text-white/70">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                )}
                <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-1 py-0.5 font-medium text-white">
                  {hotspot.title}
                </span>
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}
